import React, { useMemo, useState } from "react";

import Switch from "antd/es/switch";
import cn from "classnames";
import { useIntl } from "react-intl";

import User from "@mapmycustomers/shared/types/User";
import { userDisplayNameComparator } from "@mapmycustomers/shared/util/comparator";
import { SearchField, UserItem } from "@mapmycustomers/ui";

import useLowerCaseState from "@app/util/hook/useLowerCaseState";

import messages from "../messages";

import styles from "./UserList.module.scss";

interface Props {
  className?: string;
  onUserClick?: (userId: User["id"]) => void;
  selectedUserIdsSet: Set<User["id"]>;
  showOnlySelectedSwitch?: boolean;
  users: User[];
}

const UserList: React.FC<Props> = ({
  className,
  onUserClick,
  selectedUserIdsSet,
  showOnlySelectedSwitch = true,
  users,
}) => {
  const intl = useIntl();

  const [userQuery, setUserQuery] = useLowerCaseState("");
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);

  const filteredUsers = useMemo(
    () =>
      (userQuery
        ? users.filter(
            ({ fullName, username }) =>
              username.toLowerCase().includes(userQuery) ||
              fullName?.toLowerCase().includes(userQuery)
          )
        : [...users]
      )
        .filter((user) => !showSelectedOnly || selectedUserIdsSet.has(user.id))
        .sort(userDisplayNameComparator),
    [selectedUserIdsSet, showSelectedOnly, userQuery, users]
  );

  return (
    <div className={cn(styles.container, className)}>
      <SearchField
        onChange={setUserQuery}
        placeholder={intl.formatMessage(messages.searchUserPlaceholder)}
        size="middle"
        value={userQuery}
      />
      <div className={styles.list}>
        {filteredUsers.map((user) => (
          <UserItem
            key={user.id}
            onClick={onUserClick}
            selected={selectedUserIdsSet.has(user.id)}
            user={user}
          />
        ))}
      </div>
      {showOnlySelectedSwitch && (
        <label className={styles.onlySelectedSwitch}>
          {intl.formatMessage(messages.onlySelected)}
          <Switch checked={showSelectedOnly} onChange={setShowSelectedOnly} size="small" />
        </label>
      )}
    </div>
  );
};

export default UserList;
